/**
 * F09 v2 (§11 name-lock enforcement points 2, 5, 6) — the admin name-lock
 * override control.
 *
 * When an admin edit of a verified entity's name gets 422
 * `NAME_LOCKED_VERIFIED`, the editing form renders this panel. It explains the
 * lock and lists the ticks that hold it. Only an admin with
 * `verifications:review` sees the override control (a 10–500 character reason
 * and a resubmit button); the server also checks that permission and the host
 * route's own permission, and audits the override. Everyone else is told the
 * lock can be lifted by a reviewer, or by the owner filing a name change.
 */
import { useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Textarea } from '@bravemove/ui-web';
import {
  VERIFICATION_REASON_MAX,
  VERIFICATION_REASON_MIN,
  type NameLockOverride,
} from '@bravemove/types';
import { entityLabel } from './labels';
import { lengthError } from './validation';
import { nameLockDetailsOf } from './verificationErrors';

export interface NameLockOverridePanelProps {
  /** The `NAME_LOCKED_VERIFIED` error the last save returned. */
  readonly error: unknown;
  /** The admin holds `verifications:review` (the override is shown only then). */
  readonly canOverride: boolean;
  /** A resubmit is in flight. */
  readonly busy?: boolean;
  /** Resubmit the same edit carrying `nameLockOverride`. */
  readonly onOverride: (override: NameLockOverride) => void;
  /** Drop the lock notice (e.g. the admin reverts the name instead). */
  readonly onDismiss?: () => void;
  readonly testId?: string;
}

interface ReasonDraft {
  /** The lock error this reason was typed against. */
  readonly forError: unknown;
  readonly reason: string;
  readonly reasonError: string | undefined;
}

function emptyDraft(error: unknown): ReasonDraft {
  return { forError: error, reason: '', reasonError: undefined };
}

export function NameLockOverridePanel({
  error,
  canOverride,
  busy = false,
  onOverride,
  onDismiss,
  testId = 'name-lock-override',
}: NameLockOverridePanelProps) {
  const { t } = useI18n();
  // The typed reason belongs to ONE lock event: it is tagged with the error it
  // was typed against, so a host that keeps this panel mounted across a second
  // 422 gets an empty reason (a fresh confirmation), never a stale one.
  const [draft, setDraft] = useState<ReasonDraft>(() => emptyDraft(error));
  const current = draft.forError === error ? draft : emptyDraft(error);
  const { reason, reasonError } = current;
  const details = nameLockDetailsOf(error);
  if (!details) return null;

  const holders = details.lockedBy.map((h) => entityLabel(t, h.entityType));

  const submit = () => {
    const e = lengthError(t, reason, VERIFICATION_REASON_MIN, VERIFICATION_REASON_MAX, true);
    setDraft({ forError: error, reason, reasonError: e });
    if (e) return;
    onOverride({ reason: reason.trim() });
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <Banner tone="warning" title={t('admin:verification.nameLock.title')}>
        <p>{t('admin:verification.nameLock.body')}</p>
        {holders.length > 0 ? (
          <p className="mt-1" data-testid={`${testId}-holders`}>
            {t('admin:verification.nameLock.lockedBy', { holders: holders.join(', ') })}
          </p>
        ) : null}
      </Banner>
      {canOverride ? (
        <div className="space-y-2" data-testid={`${testId}-control`}>
          <FormField
            label={t('admin:verification.nameLock.reasonLabel')}
            helper={t('admin:verification.nameLock.reasonHelper')}
            error={reasonError}
            required
          >
            <Textarea
              value={reason}
              maxLength={VERIFICATION_REASON_MAX}
              invalid={!!reasonError}
              rows={3}
              data-testid={`${testId}-reason`}
              onChange={(e) => setDraft({ forError: error, reason: e.target.value, reasonError })}
            />
          </FormField>
          <div className="flex flex-wrap justify-end gap-2">
            {onDismiss ? (
              <Button variant="ghost" size="sm" type="button" onClick={onDismiss}>
                {t('admin:verification.common.cancel')}
              </Button>
            ) : null}
            <Button
              variant="destructive"
              size="sm"
              type="button"
              loading={busy}
              onClick={submit}
              data-testid={`${testId}-submit`}
            >
              {t('admin:verification.nameLock.submit')}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-[12px] text-ink-3" data-testid={`${testId}-no-permission`}>
          {t('admin:verification.nameLock.noPermission')}
        </p>
      )}
    </div>
  );
}
